declare const I18n;

import React, { useMemo } from "react";
import ImagePickerInput from "./inputs/ImagePickerInput";

type Category = {
  id: number;
  name: string;
  name_fr?: string;
  description?: string;
  description_fr?: string;
  icon_class?: string;
  slug: string;
};

type ImagePickOption = {
  label?: string;
  labelKey?: string;
  value: string;
  icon?: string;
  iconClass?: string;
  description?: string;
  dependants?: string[];
};

type Props = {
  block: any;
  data: Record<string, any>;
  categories?: Category[];
  onChange: (
    key: string,
    value: any,
    options?: {
      remove?: boolean;
      groupKey?: string;
    }
  ) => void;
};

const OTHER_OPTION: ImagePickOption = {
  labelKey: "common.other",
  value: "other",
  dependants: ["digital_interests_other"],
};

function resolveLocale(data: Record<string, any>): string {
  return data?.user?.language || I18n.locale || "en";
}

function buildCategoryOptions(
  categories: Category[],
  locale: string
): ImagePickOption[] {
  return categories.map((category) => {
    const isFrench = locale === "fr";

    return {
      label: isFrench ? category.name_fr || category.name : category.name,
      description: isFrench
        ? category.description_fr || category.description
        : category.description,
      value: category.slug,
    };
  });
}

function resolveOptions(
  field: any,
  categories: Category[],
  locale: string
): ImagePickOption[] {
  if (field.options === "dynamic:categories") {
    const categoryOptions = buildCategoryOptions(categories, locale);
    const hasOtherCategory = categoryOptions.some((option) => option.value === "other");

    if (hasOtherCategory) {
      return categoryOptions;
    }

    return [...categoryOptions, OTHER_OPTION];
  }

  if (Array.isArray(field.options)) {
    return field.options;
  }

  return [];
}

const ImagePickGroup: React.FC<Props> = ({ block, data, categories = [], onChange }) => {
  const sectionGroup = block.inputGroup;
  const field = block.props.fields?.[0];
  const locale = resolveLocale(data);

  const options = useMemo(
    () => (field ? resolveOptions(field, categories, locale) : []),
    [field, categories, locale]
  );

  if (!field) return null;

  if (field.options === "dynamic:categories" && categories.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        {I18n.t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }

  if (options.length === 0) return null;

  const fieldKey = field.key;
  const allowMultiple = block.props.allowMultipleSelection ?? false;

  const currentValue = sectionGroup
    ? data?.[sectionGroup]?.[fieldKey] ?? (allowMultiple ? [] : "")
    : data?.[fieldKey] ?? (allowMultiple ? [] : "");

  const writeValue = (val: any, opts: { remove?: boolean } = {}) => {
    if (sectionGroup) {
      onChange(fieldKey, val, { ...opts, groupKey: sectionGroup });
    } else {
      onChange(fieldKey, val, opts);
    }
  };

  const clearDependants = (option: ImagePickOption) => {
    option?.dependants?.forEach((depKey: string) => {
      if (sectionGroup) {
        onChange(depKey, "", { remove: true, groupKey: sectionGroup });
      } else {
        onChange(depKey, "", { remove: true });
      }
    });
  };

  const handleSelect = (val: string, option: ImagePickOption) => {
    if (allowMultiple) {
      const prevValues: string[] = Array.isArray(currentValue) ? currentValue : [];
      let newValues: string[];

      if (prevValues.includes(val)) {
        newValues = prevValues.filter((v) => v !== val);
        clearDependants(option);
      } else {
        newValues = [...prevValues, val];
      }

      writeValue(newValues);
    } else {
      const prevVal = currentValue;
      const newVal = prevVal === val ? "" : val;

      if (prevVal && prevVal !== newVal) {
        const prevOption = options.find((o) => o.value === prevVal);
        clearDependants(prevOption);
      }

      if (!newVal) {
        clearDependants(option);
      }

      if (newVal) {
        writeValue(newVal);
      } else {
        writeValue("", { remove: true });
      }
    }
  };

  const fieldLabel = field.labelKey ? I18n.t(field.labelKey) : field.label || "";

  return (
    <>
      {fieldLabel && (
        <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>{fieldLabel}</div>
      )}
      <div
        className="form-group"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          justifyContent: "center",
          marginBottom: "1.5rem",
          paddingTop: "2rem",
        }}
      >
        {options.map((option) => {
          const label = option.labelKey
            ? I18n.t(option.labelKey)
            : option.label || "";

          return (
            <ImagePickerInput
              key={option.value}
              label={label}
              description={option.description}
              keyName={fieldKey}
              value={option.value}
              currentValue={currentValue}
              icon={option.icon}
              iconClass={option.iconClass}
              onSelect={(val) => handleSelect(val, option)}
            />
          );
        })}
      </div>
    </>
  );
};

export default ImagePickGroup;
